import { ArticlePollOption } from './article-poll-option.interface';
import { BasicDocument } from './document.interface';
import { EventParticipations } from './event-participations.interface';
import {
  Base64String,
  FileKeyUploadParams,
  FileUploadParams
} from './file.interface';
import { Group } from './group.interface';
import { Picture } from './picture.interface';
import { Settlement } from './settlement.interface';
import { User } from './user.interface';

export interface ArticlesResponse {
  total_pages: number;
  articles: Article[];
}

// ArticleEntity in BE

export type Article =
  | ArticleArticle
  | ArticleEvent
  | ArticlePoll
  | ArticleMarketplace;

export interface ArticleBase {
  id: number;
  title: string;
  content_html: string;
  article_type: ArticleType;
  user: User;
  comment_count: number;
  like_count: number;
  article_cover_picture?: Picture;
  picture: string;
  picture_preview?: string;
  pictures: Picture[];
  important?: boolean;
  video_url?: string;
  pinned: boolean;
  /**
   * if pinned
   */
  pin_until?: Date;
  liked?: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  is_draft: boolean;
  content_markdown?: string;
  show_in_feed: boolean;
  groups: Group[];
  settlements: Settlement[];
  publish_globally: boolean;
  highlighted: boolean;
  comments_enabled: boolean;
}

export interface ArticleArticle extends ArticleBase {
  article_type: ArticleType.ARTICLE;
  documents: ArticleDocument[];
}

export interface ArticleEvent extends ArticleBase {
  article_type: ArticleType.EVENT;
  location: string;
  start_at: string;
  end_at: string | null;
  documents: ArticleDocument[];
  event_participations: EventParticipations;
  external_participation_token?: string;
  registration_ends_at: string | null;
}

export interface ArticlePoll extends ArticleBase {
  article_type: ArticleType.POLL;
  poll_options: ArticlePollOption[];
  documents: ArticleDocument[];
}

export interface ArticleMarketplace extends ArticleBase {
  article_type: ArticleType.MARKETPLACE;
  price: number;
  phone: string;
  marketplace_type: MarketplaceType;
  email: string;
  documents: ArticleDocument[];
}

/**
 * Article documents are special and doesn't extend `DocumentEntity`
 * in the BE. It shares some properties tho.
 */
export type ArticleDocument = Pick<
  BasicDocument,
  'id' | 'url' | 'content_type' | 'title'
>;

export enum ArticleType {
  ARTICLE = 'article',
  EVENT = 'event',
  MARKETPLACE = 'marketplace',
  VOTE = 'vote',
  POLL = 'poll'
}

export enum MarketplaceType {
  SELL = 'sell',
  BUY = 'buy'
}

/*
 * ArticleParams – permitted params for update/create
 *
 */

export interface ArticleParams {
  article_type: ArticleType;
  content_markdown: string;
  title?: string;
  price?: number;
  marketplace_type?: MarketplaceType;
  group_ids: number[];
  settlement_ids: number[];
  poll_options?: ArticlePollOption[];
  video_url?: string;
  article_cover_picture?: Base64String;
  pictures?: Base64String[];
  documents?: ArticleDocumentParams[];
  deleted_pictures?: number[];
  deleted_documents?: number[];
  start_at?: string;
  end_at?: string;
  location?: string;
  important?: boolean;
  show_in_feed?: boolean;
  publish_globally: boolean;
  registration_ends_at?: string;
  published_at?: string | null;
  is_draft?: boolean;
  highlighted?: boolean;
  comments_enabled?: boolean;
}

export type ArticleDocumentParams =
  | Pick<ArticleDocument, 'id' | 'title'>
  | FileUploadParams
  | FileKeyUploadParams;

/*
 * Params for filtering articles
 */

export interface ArticleFilterParams {
  origin?: 'all' | 'my_settlement';
  'group_ids[]'?: number[];
  'article_types[]'?: ArticleTypeFilterValue[];
  order_by?: string;
  filter_by_show_in_feed?: boolean;
  /**
   * If present, fetches articles that the current user has created.
   */
  for_current_user?: 'true' | (string & {});
  period_end?: string; // date in format 'YYYY-MM-DD HH:MM:SS'
  period_start?: string; // date in format 'YYYY-MM-DD HH:MM:SS'
  /**
   * If present, fetches articles that are only visible to members.
   */
  member_only?: 'true' | (string & {});
  past_events?: boolean;
  future_events?: boolean;
  /**
   * If true, fetches only events that have already ended, newest first when ordered by start_at.
   */
  ended_events?: boolean;
  /**
   * If present, fetches articles events that the current user is interested in or participating in.
   */
  interested?: 'true' | (string & {});
  /**
   * Filter by publish status. `published` returns only published articles;
   * `in_progress` returns drafts and scheduled articles.
   */
  status?: 'published' | 'in_progress';
  /**
   * If true, restricts results to highlighted articles (offizielle Mitteilung).
   */
  filter_by_highlighted?: boolean;
}

export interface ArticleEventFilter {
  groupIds?: number[];
  origin?: 'all' | 'my_settlement';
  period?: Period;
  memberOnly?: boolean;
  periodStart?: Date;
  periodEnd?: Date;
}

export type Period = 'all' | 'today' | 'week' | 'month' | 'past';

export enum ArticleTypeFilterValue {
  ARTICLE = 'article',
  POLL = 'poll',
  EVENT = 'event',
  MARKETPLACE = 'marketplace'
}

export const INITIAL_EVENT_FILTERS: ArticleEventFilter = {
  period: 'all',
  memberOnly: false,
  origin: 'all'
};

export interface ArticleDeleteParams {
  reason_for_deletion?: string;
  cancellation_reason?: string;
}
